import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#connectAccountMenu();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#accountAbort?.abort();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }


  #accountAbort;

  updatedCallback() {
    super.updatedCallback();
    this.#connectAccountMenu();
  }

  #connectAccountMenu() {
    this.#accountAbort?.abort();
    const trigger = this.querySelector('.vanity-account-entry__trigger');
    const panel = this.querySelector('.vanity-account-entry__panel');
    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
    this.#accountAbort = new AbortController();
    const { signal } = this.#accountAbort;
    const position = () => {
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(340, window.innerWidth - 24);
      const top = Math.max(12, rect.bottom + 8);
      const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
      panel.style.top = top + 'px';
      panel.style.left = left + 'px';
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
      panel.style.width = width + 'px';
      panel.style.maxHeight = Math.max(100, window.innerHeight - top - 12) + 'px';
    };
    panel.addEventListener('beforetoggle', (event) => {
      if (event.newState === 'open') position();
    }, { signal });
    const onScroll = (event) => {
      if (!panel.matches(':popover-open') || panel.contains(event.target)) return;
      const rect = trigger.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) panel.hidePopover();
      else position();
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true, signal });
    window.addEventListener('resize', () => {
      if (panel.matches(':popover-open')) position();
    }, { passive: true, signal });
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
